// BFSG
import * as React from 'react'
import styled from 'styled-components'
import Accordion from 'theme/molecules/Accordion'
import Faktor from 'assets/factor.svg'
import { useDisplayVariant } from 'modules/productDetail'
import theme from 'theme'
import Download from 'assets/download.svg'
import config from 'config'
import Markdown from 'theme/atoms/Markdown'
import useTranslations from 'hooks/useTranslations'
import Button from 'theme/atoms/Button'
import B2cSwitch from 'theme/atoms/B2cSwitch'
import useAttributes from './useAttributes'
import * as evt from './events'
import Rating from 'theme/molecules/Rating'

type Props = {
  productPath: string
}
export default function InformationBox(props: Props) {
  const t = useTranslations<'templates-PDP'>()
  const variant = useDisplayVariant()
  const attributes = useAttributes()

  const featureValues = variant.data.attributes.FEATURE?.values || []
  const showManufacturerInfo =
    variant.data.attributes.MANUFACTURER_NAME ||
    variant.data.attributes.MANUFACTURER_ADDRESS ||
    variant.data.attributes.MANUFACTURER_MAIL

  return (
    <Wrapper
      className="InformationBox"
      $isFetching={!!variant.data.isDummy}
      $aiDescriptionGiven={!!variant.data.descriptionAI}
      $wdv2900AIDescriptionTest={config.abTests.wdv2900AIDescriptionTest}
    >
      <div className="top">
        <div className="description-left">
          {config.abTests.wdv2900AIDescriptionTest && (
            <>
              <div data-cy-state className="description-a">
                <Markdown html={variant.data.description} />
              </div>
              <div data-cy-state className="description-b">
                <Markdown
                  html={variant.data.descriptionAI || variant.data.description}
                />
              </div>
            </>
          )}
          {!config.abTests.wdv2900AIDescriptionTest && (
            <>
              <div className="description">
                <Markdown html={variant.data.description} />
              </div>
            </>
          )}
        </div>
        <div className="additional-info">
          <B2cSwitch.B2B>
            {variant.data.specimen?.hasSpecimenProducts?.length > 0 && (
              <div className="hasSpecimen" data-cy-state="show-speciment">
                <Button
                  variation="secondary"
                  to={`/pdp/${variant.data.specimen.hasSpecimenProducts[0]}`}
                  $fullWidth
                  onClick={() => evt.orderSpecimenClick()}
                  data-cy-handle="order-specimen-button"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      evt.orderSpecimenClick()
                      window.open(
                        `/pdp/${variant.data.specimen.hasSpecimenProducts[0]}`
                      )
                    }
                  }}
                >
                  {t('pdp_article_has_specimen_text')}
                </Button>
              </div>
            )}
          </B2cSwitch.B2B>
          <div className="article-number">
            {t('pdp_article_number_text')}
            {variant.data.sku}
          </div>
          {featureValues.length > 0 && (
            <ul className="usps" data-cy-state="show-feature-list">
              {featureValues.map((el, i) => (
                <li key={i}>
                  <Faktor />
                  {el.value}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      {variant.data.productReviews?.productReviewAvg &&
        variant.data.productReviews.productReviewCount && (
          <div
            className="rating-container"
            data-cy-state="show-informationbox-rating"
          >
            <div className="rating-headline">{t('ratings_headline')}</div>
            <Rating
              containerID={variant.data.containerID}
              ratingAverage={variant.data.productReviews.productReviewAvg}
              ratingCount={variant.data.productReviews.productReviewCount}
              amountText={t('rating_amount_text', {
                COUNT: variant.data.productReviews.productReviewCount
              })}
              clickable
              evtSource="product_description"
            />
          </div>
        )}
      <div className="accordion-list">
        {attributes.icons.length > 0 && (
          <div className="accordion-container" data-cy-state="show-icons">
            <Accordion disableToggle>
              <IconList>
                {attributes.icons.map((icon, i) => (
                  <img
                    key={i}
                    alt={icon.label}
                    title={icon.label}
                    src={icon.icon}
                  />
                ))}
              </IconList>
            </Accordion>
          </div>
        )}
        {attributes.labels.length > 0 && (
          <div
            className="accordion-container"
            data-cy-state="show-information-labels"
          >
            <Accordion
              label={t('product_informations').props.children}
              eventVisible={() =>
                evt.productInformationAccordionVisible(
                  props.productPath,
                  variant.data.title,
                  variant.data.sku
                )
              }
              eventClick={() =>
                evt.productInformationAccordionClick(
                  props.productPath,
                  variant.data.title,
                  variant.data.sku
                )
              }
            >
              {attributes.labels.map((attribute, i) => (
                <InformationGrid key={'attributes' + i} role="rowgroup">
                  <div className="flex-row" role="cell">
                    {attribute.label}
                  </div>
                  <div className="flex-row" role="cell">
                    {attribute.value}
                  </div>
                </InformationGrid>
              ))}
            </Accordion>
          </div>
        )}
        {variant.data.documents && variant.data.documents.length > 0 && (
          <div className="accordion-container" data-cy-state="show-downloads">
            <Accordion label={t('downloads')}>
              {variant.data.documents?.map((document) => {
                return (
                  <DocumentWrapper key={document.title}>
                    {
                      <a
                        href={config.modules.cloudinary.endpoint + document.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            window.open(
                              config.modules.cloudinary.endpoint + document.url,
                              '_blank',
                              'noopener noreferrer'
                            )
                          }
                        }}
                      >
                        <Download />
                        <span>{document.title}</span>
                      </a>
                    }
                  </DocumentWrapper>
                )
              })}
            </Accordion>
          </div>
        )}
        {/* New Accordion for Manufacturer Information */}
        {showManufacturerInfo && (
          <div
            className="accordion-container"
            data-cy-state="show-manufacturer-info"
          >
            <Accordion label={t('manufacturer_info')}>
              {attributes.manufacturerName && (
                <InformationGrid
                  role="rowgroup"
                  data-cy-state="show-manufacturer-info-manufacturerName"
                >
                  <div className="flex-row" role="cell">
                    {t('manufacturer_name')}:
                  </div>
                  <div className="flex-row" role="cell">
                    {attributes.manufacturerName}
                  </div>
                </InformationGrid>
              )}

              {attributes.manufacturerAddress && (
                <InformationGrid
                  role="rowgroup"
                  data-cy-state="show-manufacturer-info-manufacturerAddress"
                >
                  <div className="flex-row" role="cell">
                    {t('manufacturer_address')}:
                  </div>
                  <div className="flex-row" role="cell">
                    {(attributes.manufacturerAddress as string)
                      .split(',')
                      .map((line, index) => (
                        <div key={index}>{line}</div>
                      ))}
                  </div>
                </InformationGrid>
              )}

              {attributes.manufacturerMail && (
                <InformationGrid
                  role="rowgroup"
                  data-cy-state="show-manufacturer-info-manufacturerMail"
                >
                  <div className="flex-row" role="cell">
                    {t('manufacturer_mail')}:
                  </div>
                  <div className="flex-row" role="cell">
                    {attributes.manufacturerMail}
                  </div>
                </InformationGrid>
              )}
            </Accordion>
          </div>
        )}
      </div>
    </Wrapper>
  )
}

const DocumentWrapper = styled.div`
  display: flex;
  align-items: flex-end;
  > a {
    > svg {
      height: 1.2rem;
      width: 1.2rem;
    }
    > span {
      margin-left: ${theme.spacing('s')};
    }
  }
`
const Wrapper = styled.div<{
  $isFetching: boolean
  $aiDescriptionGiven: boolean
  $wdv2900AIDescriptionTest: boolean
}>`
  ${(props) =>
    props.$isFetching &&
    `
      * {
          color: transparent !important;
          text-shadow: 0 0 0.9375rem rgba(0,0,0,0.5) !important;
      }
  `}
  > .top {
    display: flex;
    flex-direction: column-reverse;
    margin-bottom: ${theme.spacing('ml')};
    @media (min-width: ${theme.ms.XL}px) {
      flex-direction: row;
      margin-bottom: ${theme.spacing('l')};
    }
    > .description-left {
      flex: 1;

      > .description,
      .description-a {
        flex: 1;
        ${theme.ty('r-s')}

        @media (min-width: ${theme.ms.SM}px) {
          ${theme.ty('r-base')}
        }
      }
      > .description-b {
        display: none;
      }
    }

    > .additional-info {
      min-width: 13.625rem;
      margin-bottom: ${theme.spacing('m')};
      ${theme.ty('rc-s')}
      > span > .hasSpecimen {
        margin-bottom: ${theme.spacing('ml')};
        > .secondary {
          padding: 0.625rem 3.25rem;
        }
      }
      @media (min-width: ${theme.ms.SM}px) {
        ${theme.ty('rc-base')}
      }
      @media (min-width: ${theme.ms.XL}px) {
        margin-left: 3.125rem;
        margin-bottom: 0;
      }
      > .article-number {
        background: ${theme.colors.shade.b6};
        text-align: center;
        width: 100%;
        padding: 0.625rem 0;
        margin-bottom: ${theme.spacing('m')};
      }

      > ul.usps {
        @media (min-width: ${theme.ms.XL}px) {
          max-width: 15rem;
        }
        > li {
          margin-bottom: ${theme.spacing('s')};
          &:last-child {
            margin-bottom: 0;
          }
          display: flex;
          align-items: center;
          > svg {
            margin-right: 0.75rem;
            min-width: 1rem;
            > g > g > circle {
              fill: ${theme.colors.green_50};
            }
          }
        }
      }
    }
  }

  > .rating-container {
    display: none;
    align-items: center;
    margin-bottom: ${theme.spacing('xl')};
    display: flex;
    > .rating-headline {
      margin-right: ${theme.spacing('m')};
      ${theme.ty('rc-base')}
      @media (min-width: ${theme.ms.SM}px) {
        ${theme.ty('rc-xl')}
      }
    }
  }

  > .accordion-list > .accordion-container {
    border-top: 1px solid ${theme.colors.shade.b3};
    &:last-child {
      border-bottom: 1px solid ${theme.colors.shade.b3};
    }
  }

  ${(props) =>
    props.$aiDescriptionGiven &&
    props.$wdv2900AIDescriptionTest &&
    `body.ab-mode-b & {
  > .top {
    > .description-left{
      > .description-a {
        display: none;
      }
      > .description-b {
        display: unset;
        flex: 1;
        ${theme.ty('r-s')}

        @media (min-width: ${theme.ms.SM}px) {
          ${theme.ty('r-base')}
        }
      }
    }
  }
    }  `}
`

const IconList = styled.div`
  display: flex;
  > img {
    margin: 0 ${theme.spacing('xxs')};
    width: 2.5rem;
    height: 2.5rem;
  }
`

const InformationGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, 50%);
  grid-template-rows: 100% auto;
  transition: 0.5s;
  &:nth-child(odd) > .flex-row {
    background: ${theme.colors.shade.b7};
  }
  > .flex-row {
    display: block;
    width: 100%;
    text-align: left;
    padding: ${theme.spacing('m')};
  }
`
